import type { BulkBatch, BulkBatchStatus, BulkRowStatus, BulkSubjectType, BulkTemplateColumn } from '@/api/bulk';

/** The bulk surface's pure rules, kept apart from React so they can be tested on their own. */

export const SUBJECT_TYPE_LABEL: Readonly<Record<BulkSubjectType, string>> = {
  INDIVIDUAL: 'Individuals',
  CORPORATE: 'Companies',
  CONNECTION_GROUP: 'Connection groups',
};

export const SUBJECT_TYPE_HELP: Readonly<Record<BulkSubjectType, string>> = {
  INDIVIDUAL: 'One person per row.',
  CORPORATE: 'One company per row.',
  CONNECTION_GROUP: 'One line per row. Rows with the same email become one person and one run.',
};

export const BATCH_STATUS_LABEL: Readonly<Record<BulkBatchStatus, string>> = {
  AWAITING_CONFIRMATION: 'Waiting for you',
  NOTHING_TO_START: 'Nothing to start',
  RUNNING: 'Starting runs',
  COMPLETED: 'Done',
  COMPLETED_WITH_FAILURES: 'Done, with failures',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

export const BATCH_STATUS_COLOR: Readonly<Record<BulkBatchStatus, string>> = {
  AWAITING_CONFIRMATION: 'gold',
  NOTHING_TO_START: 'default',
  RUNNING: 'processing',
  COMPLETED: 'success',
  COMPLETED_WITH_FAILURES: 'warning',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

export const ROW_STATUS_LABEL: Readonly<Record<BulkRowStatus, string>> = {
  INVALID: 'Has errors',
  VALID: 'Ready',
  DUPLICATE: 'Already started',
  GROUPED: 'In a group',
  QUEUED: 'Queued',
  STARTED: 'Started',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

export const ROW_STATUS_COLOR: Readonly<Record<BulkRowStatus, string>> = {
  INVALID: 'error',
  VALID: 'blue',
  DUPLICATE: 'default',
  GROUPED: 'default',
  QUEUED: 'processing',
  STARTED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

/** Rows that have reached the end of their road. */
export function settledRows(batch: BulkBatch): number {
  const c = batch.counts;
  return c.invalid + c.duplicate + c.grouped + c.started + c.failed + c.cancelled + c.expired;
}

/** Progress through the file, 0 to 100. */
export function progressPercent(batch: BulkBatch): number {
  if (batch.totalRows === 0) return 0;
  return Math.min(100, Math.round((settledRows(batch) / batch.totalRows) * 100));
}

/** The batch is still changing, so its page keeps polling. */
export function isLive(status: BulkBatchStatus): boolean {
  return status === 'RUNNING';
}

export function canConfirm(batch: BulkBatch): boolean {
  return batch.status === 'AWAITING_CONFIRMATION' && batch.counts.valid > 0;
}

export function canCancel(batch: BulkBatch): boolean {
  return batch.status === 'AWAITING_CONFIRMATION' || batch.status === 'RUNNING';
}

export function canRetry(batch: BulkBatch): boolean {
  return batch.status === 'COMPLETED_WITH_FAILURES';
}

/** What will start when the user confirms, in words. */
export function confirmSummary(batch: BulkBatch): string {
  const { valid, invalid, duplicate, grouped } = batch.counts;
  const parts = [`${valid} run${valid === 1 ? '' : 's'} will start`];
  if (grouped > 0) parts.push(`${grouped} more line${grouped === 1 ? '' : 's'} grouped into them`);
  if (invalid > 0) parts.push(`${invalid} row${invalid === 1 ? '' : 's'} with errors will be skipped`);
  if (duplicate > 0) parts.push(`${duplicate} row${duplicate === 1 ? '' : 's'} already started will be skipped`);
  return `${parts.join('. ')}.`;
}

/** Whether a column is required, given whether room links are going out. */
export function columnRequired(column: BulkTemplateColumn, inviting: boolean): boolean {
  if (column.need === 'ALWAYS') return true;
  if (column.need === 'IF_INVITING') return inviting;
  if (column.need === 'IF_NOT_INVITING') return !inviting;
  return false;
}

const MIN_REASON = 10;

export function repeatReasonProblem(reason: string): string | null {
  const length = reason.trim().length;
  if (length < MIN_REASON) return `Say why, in at least ${MIN_REASON} characters. This goes on the record.`;
  if (length > 500) return 'Keep the reason under 500 characters.';
  return null;
}

/** The file's name without its extension: a reasonable default name for the batch. */
export function defaultBatchName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '').slice(0, 200);
}
